import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Instagram, ArrowLeft, ExternalLink, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { companyInfo } from '../mock';
import SEO from './SEO';
import { breadcrumbSchema } from '../seo/schemas';

const Gallery = () => {
  const instagramUsername = 'jetcoelectricalcontractor';
  const instagramUrl = 'https://www.instagram.com/jetcoelectricalcontractor';

  // Sample project categories
  const projectCategories = [
    { name: 'EV Charger Installations', count: '15+ Projects' },
    { name: 'Panel Upgrades', count: '25+ Projects' },
    { name: 'Smart Home Systems', count: '10+ Projects' },
    { name: 'Commercial Projects', count: '20+ Projects' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title="Project Gallery | Brooklyn Electrical Work Portfolio"
        description="Browse Jetco Electrical Contractors' completed projects in Brooklyn, Queens, and Manhattan. EV chargers, panel upgrades, smart home installations, and commercial electrical work portfolio."
        path="/gallery"
        keywords="electrician portfolio brooklyn, electrical work gallery, EV charger installation photos, panel upgrade projects"
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Gallery', path: '/gallery' },
          ]),
        ]}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-400/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts/x1j92naa_Untitled%20design.png" 
                alt="Jetco Electrical" 
                className="h-12 w-auto"
              />
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-[0_0_15px_rgba(255,215,0,0.3)]">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-black relative">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl"></div>
        </div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-cyan-400 hover:text-yellow-400 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Work <span className="text-transparent bg-clip-text bg-yellow-400">Gallery</span>
            </h1>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-4"></div>
            <p className="text-xl text-gray-400 mb-8">
              See our completed electrical projects across Brooklyn, Queens, and Manhattan
            </p>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-[0_0_20px_rgba(236,72,153,0.3)]">
                <Instagram className="w-5 h-5 mr-2" />
                Follow @{instagramUsername}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Project Categories */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Project <span className="text-transparent bg-clip-text bg-yellow-400">Categories</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {projectCategories.map((category) => (
              <Card key={category.name} className="text-center hover:shadow-[0_0_30px_rgba(255,215,0,0.2)] transition-shadow border-2 border-gray-800 hover:border-yellow-400/50 bg-gray-900">
                <CardHeader>
                  <CardTitle className="text-lg text-white">{category.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-transparent bg-clip-text bg-yellow-400">{category.count}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full mb-4 shadow-[0_0_20px_rgba(236,72,153,0.3)]">
              <Instagram className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Follow Us on <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">Instagram</span></h2>
            <p className="text-lg text-gray-400 mb-6">See our latest projects and electrical work</p>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
              <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 hover:text-yellow-400 transition-all">
                @{instagramUsername}
              </span>
            </a>
          </div>

          {/* Instagram Embed */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900 rounded-2xl shadow-[0_0_30px_rgba(255,215,0,0.1)] border border-gray-800 p-8 text-center">
              <p className="text-gray-400 mb-6">
                Visit our Instagram profile to see our latest electrical projects, installations, and behind-the-scenes work.
              </p>
              
              {/* Instagram Profile Link */}
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <Card className="hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] transition-shadow border border-gray-800 bg-black">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-yellow-400 mb-2">200+</div>
                    <div className="text-gray-400">Projects Completed</div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] transition-shadow border border-gray-800 bg-black">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-yellow-400 mb-2">15+</div>
                    <div className="text-gray-400">Years Experience</div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] transition-shadow border border-gray-800 bg-black">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-yellow-400 mb-2">100%</div>
                    <div className="text-gray-400">Satisfaction Rate</div>
                  </CardContent>
                </Card>
              </div>

              <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-[0_0_20px_rgba(236,72,153,0.3)]">
                  <Instagram className="w-5 h-5 mr-2" />
                  View Our Instagram Gallery
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>

              <p className="text-sm text-gray-500 mt-4">
                Follow us for daily updates on our electrical projects across Brooklyn
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-900 mb-8">
            Get a free estimate for your electrical needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/?scroll=contact">
              <Button size="lg" className="bg-black text-white hover:bg-gray-900">
                Get Free Estimate
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-yellow-400/30 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2025 {companyInfo.name}. All rights reserved.</p>
          <div className="mt-4">
            <a href={`tel:${companyInfo.phone}`} className="text-yellow-400 hover:text-cyan-400 transition-colors">{companyInfo.phone}</a>
            <span className="mx-3 text-gray-600">|</span>
            <a href={`mailto:${companyInfo.email}`} className="text-cyan-400 hover:text-yellow-400 transition-colors">{companyInfo.email}</a>
          </div>
          <div className="mt-4">
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-pink-400 hover:text-purple-400 transition-colors">
              <Instagram className="w-5 h-5 mr-2" />
              @{instagramUsername}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Gallery;
